import React, { useState } from 'react';
import { event } from '../../analytics/analytics';
import { SAMPLE_QUESTION } from '../../config/landingProof';

// STUD-899: a real question from the bank that a visitor can answer without signing up, with
// the same instant feedback + step-by-step explanation the app gives. Purely client-side.
export default function SampleQuestion() {
  const q = SAMPLE_QUESTION;
  const [selected, setSelected] = useState('');
  const [checked, setChecked] = useState(false);
  const isCorrect = selected === q.correctKey;

  const check = () => {
    if (!selected) return;
    setChecked(true);
    event({ category: 'Landing Page', action: 'Sample Question Answered', label: isCorrect ? 'correct' : 'incorrect' });
  };

  const reset = () => {
    setSelected('');
    setChecked(false);
  };

  const optionClass = (key) => {
    const base = 'flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm leading-6 transition';
    if (!checked) {
      return `${base} ${selected === key
        ? 'border-[var(--color-primary)] bg-[var(--color-accent-subtle)]'
        : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[var(--border-accent)]'}`;
    }
    if (key === q.correctKey) return `${base} border-[var(--color-success)] bg-[var(--color-success-subtle)]`;
    if (key === selected) return `${base} border-[var(--color-danger)] bg-[var(--color-danger-subtle)]`;
    return `${base} border-[var(--border-default)] bg-[var(--surface-card)] opacity-70`;
  };

  return (
    <section
      aria-labelledby="sample-question-heading"
      data-testid="sample-question"
      className="rounded-2xl bg-[var(--surface-card)] p-4 text-left shadow-[var(--shadow-lg)] ring-1 ring-[var(--border-default)] md:p-6"
    >
      <div className="text-2xs font-semibold uppercase tracking-widest text-[var(--sage-700)]">
        Try a real question · {q.subject}
      </div>
      <h2 id="sample-question-heading" className="font-heading mt-2 text-xl font-semibold text-[var(--text-primary)]">
        No sign-up needed
      </h2>
      <div className="mt-3 space-y-1 text-sm leading-6 text-[var(--text-secondary)]">
        {q.stimulus.map((line) => <p key={line} className="m-0">{line}</p>)}
      </div>
      <p className="mt-3 text-base font-semibold leading-7 text-[var(--text-primary)]">{q.prompt}</p>

      <div role="radiogroup" aria-label="Answer options" className="mt-4 space-y-2">
        {Object.entries(q.options).map(([key, text]) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected === key}
            disabled={checked}
            onClick={() => setSelected(key)}
            className={optionClass(key)}
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--bg-subtle)] text-xs font-bold text-[var(--text-primary)]">
              {key}
            </span>
            <span className="min-w-0 text-[var(--text-primary)]">{text}</span>
          </button>
        ))}
      </div>

      {!checked ? (
        <button
          type="button"
          onClick={check}
          disabled={!selected}
          className="tc-btn tc-btn--accent tc-btn--md mt-4 w-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          Check my answer
        </button>
      ) : (
        <div className="mt-4" role="status">
          <p className="font-semibold text-[var(--text-primary)]">
            {isCorrect ? 'Correct — nicely reasoned.' : `Not quite. The answer is ${q.correctKey}.`}
          </p>
          <div className="mt-3 rounded-lg bg-[var(--bg-subtle)] p-4">
            <div className="text-2xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">Step-by-step explanation</div>
            {q.theory && <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{q.theory}</p>}
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-6 text-[var(--text-secondary)]">
              {q.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
            <p className="mt-3 text-xs italic text-[var(--text-muted)]">
              This is the explanation the app shows for this question. Explanations are AI-generated and may contain mistakes.
            </p>
          </div>
          <button type="button" onClick={reset} className="tc-btn tc-btn--secondary tc-btn--md mt-4 w-full">
            Try it again
          </button>
        </div>
      )}
    </section>
  );
}
